// @flow
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import * as React from 'react'

import { colors, media } from '@toggl/style'

import { IconArrowBigLeft } from '../icons/IconArrow'
import { IconFacebook } from '../icons/IconFacebook'
import { IconLinkedIn } from '../icons/IconLinkedIn'
import { IconTwitter } from '../icons/IconTwitter'
import * as text from '../text'

declare var globalThis: any

type Props = {
  goBackLink?: string,
  title?: string | React.Node,
  socialText?: string,
  backText?: string,
  tweetTextToShare?: string,
  lnTextToShare?: string,
  urlToShare?: string,
  background?: string,
  align?: string,
  className?: string,
}

export const BackSocial = ({
  goBackLink,
  title,
  socialText,
  backText,
  tweetTextToShare,
  lnTextToShare,
  urlToShare,
  align,
  className,
  ...props
}: Props): null | React.Element<typeof Root> => {
  const theme = useTheme()

  return (
    <Root className={className} {...props}>
      <Wrapper>
        <Header align={align}>
          {backText && (
            <BackWrapper align={align}>
              <LinkStyled to={goBackLink}>
                <IconArrowBigLeft color={theme.primary} />

                <HeaderText>{backText}</HeaderText>
              </LinkStyled>
            </BackWrapper>
          )}

          {urlToShare && (
            <DesktopSubHeaderContent>
              <SocialIcons
                socialText={socialText}
                urlToShare={urlToShare}
                tweetTextToShare={tweetTextToShare}
                lnTextToShare={lnTextToShare}
              />
            </DesktopSubHeaderContent>
          )}
        </Header>

        {title && (
          <MobileSubHeaderContent>
            {typeof title === 'string' && (
              <PositionTitle>{title}</PositionTitle>
            )}
            {typeof title !== 'string' && title}

            {urlToShare && (
              <SocialIcons
                socialText={socialText}
                urlToShare={urlToShare}
                tweetTextToShare={tweetTextToShare}
                lnTextToShare={lnTextToShare}
              />
            )}
          </MobileSubHeaderContent>
        )}
      </Wrapper>
    </Root>
  )
}

const Root = styled<{
  background?: string,
  className?: string,
  children: React.Node,
}>('div')`
  width: 100%;
  background: ${(p) => p.background || colors.darkPurple};
`
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1230px;
  margin: auto;
  ${media.mq[1]} {
    padding: 60px 30px 45px;
    flex-direction: row;
  }
`

const SocialIcons = ({
  socialText,
  urlToShare,
  tweetTextToShare,
  lnTextToShare,
}: {
  socialText?: string,
  urlToShare?: string,
  tweetTextToShare?: string,
  lnTextToShare?: string,
}) => {
  const shareUrl = urlToShare ?? 'https://toggl.com/jobs/'
  const tweeterShareText =
    tweetTextToShare ??
    'Great news! @toggl is hiring remotely for your dream job! Check it out:'
  const linkedInShareText =
    lnTextToShare ?? 'Toggl is hiring remotely for your dream job!'

  return (
    <SocialIconsStyled>
      <P>{socialText || 'Share this job'}</P>

      <div>
        <Link
          rel="noopener"
          target="_blank"
          href={getTwitterLink(tweeterShareText, shareUrl)}
        >
          <IconTwitter />
        </Link>

        <Link rel="noopener" target="_blank" href={getFacebookLink(shareUrl)}>
          <IconFacebook />
        </Link>

        <Link
          rel="noopener"
          target="_blank"
          href={getLinkedinLink(linkedInShareText, shareUrl)}
        >
          <IconLinkedIn />
        </Link>
      </div>
    </SocialIconsStyled>
  )
}

const H = styled.h2`
  ${text.heading2};
  color: ${colors.beige};
  text-align: center;
`

const P = styled.span`
  ${text.paragraph3};
  color: ${colors.beige};
`

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;

  svg {
    width: 32px;
  }

  &:hover span {
    color: ${(p) => p.theme.primary};
  }
`

const PositionTitle = styled(H)`
  width: 249px;
  text-align: center;
  margin-top: 13px;
`

const SocialIconsStyled = styled.div`
  margin-top: 40px;

  div {
    display: flex;
    justify-content: space-between;
    margin-top: 13px;
    min-width: 5rem;
  }

  a:hover svg {
    fill: ${(p) => p.theme.primary};
  }

  ${media.mq[1]} {
    margin-top: 0;
    margin-left: auto;
    display: flex;
    justify-content: space-between;

    div {
      margin: 0;
    }

    > span {
      margin-right: 25px;
    }

    > div {
      display: flex;
      align-items: center;
    }
  }
`

const DesktopSubHeaderContent = styled.div`
  display: none;

  ${media.mq[1]} {
    display: flex;
  }
  flex-direction: column;
`

const MobileSubHeaderContent = styled.div`
  background: transparent;
  padding: 30px 30px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 80px;
  margin-top: 30px;
  ${media.mq[1]} {
    display: none;
  }
`

const HeaderText = styled(P)`
  margin-left: 20px;
  color: ${colors.beige};
`

const Header = styled.div`
  width: 100%;
  ${media.mq[1]} {
    display: flex;
    justify-content: ${(p) => p.align || 'space-between'};
  }
`

export const BackWrapper: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 30px;
  border-bottom: 1px solid ${colors.fadedPurple};
  justify-content: ${(p) => p.align || ' '};
  ${media.mq[1]} {
    border-bottom: none;
    padding: 0;
    justify-content: space-between;
  }
`

const getTwitterLink = (text: string, url: string) => {
  const twitterLink = new URL('https://twitter.com/intent/tweet')
  twitterLink.searchParams.set('text', text)
  twitterLink.searchParams.set('url', url)
  return twitterLink.href
}

const getFacebookLink = (url: string) => {
  const facebookLink = new URL('https://www.facebook.com/sharer/sharer.php')
  facebookLink.searchParams.set('u', url)
  return facebookLink.href
}

const getLinkedinLink = (text: string, url: string) => {
  const linkedinLink = new URL('https://www.linkedin.com/shareArticle')
  linkedinLink.searchParams.set('mini', 'true')
  linkedinLink.searchParams.set('title', text)
  linkedinLink.searchParams.set('url', url)
  return linkedinLink.href
}
